// 2026-05-26 - FormuLearning - Step 3 §3.2 X 변수 표
// 2026-08-31 수정 - 이슈 #58 S5(#68), `M84-09`·`M84-10`: 열 순서를 정본대로
//                   **`Min (mg)`·`Max (mg)`·`Min %`·`Max %`** 로 바꾸고 **mg 칸을 입력 개방**한다.
//                   종전은 `Min %`·`Min (mg)`·`Max %`·`Max (mg)` 순에 mg 가 읽기 전용이었다.
//                   *"허가에서는 한 정의 몇%… 그러나 실제 실험은 mg 기준으로 한다"*(회의록 260804 L37~38).
//                   **저장 정본은 `min_pct`·`max_pct` 하나뿐이다** — mg 는 총중량에서 나오는
//                   파생값이라, 둘 다 저장하면 총중량이 바뀌는 순간 어느 쪽이 정본인지 알 수 없어진다.
// 2026-09-24 수정 - 이슈 #75(G4): **주성분(API) 행 잠금 해제**(`M901-15`·`16`, `BR-S3-11`) — 기능 목록에
//                   `Active (API)` 추가, `is_active` 행도 기능·이름·mode·순서·삭제를 모두 편집한다.
//                   `is_active` 행을 지우면 다음 `Active (API)` 행이 승계한다(`Q-260916-06`).
//                   **`Pull from Reference Drug` 버튼 폐지**(`M901-14`) — 대조약 선택 시 묻는다(`BR-S3-10`).
// 2026-11-02 수정 - 이슈 #101(BR-S3-03): Balance 가 있으면 고정 + selected 하한 합 ≤ 99%(balance 하한 1% 자리).
// 파일 위치: frontend/src/features/step3/XVariableSection.tsx

import { useEffect, useState } from 'react'
import type { KeyboardEvent } from 'react'
import type { XVariable, XMode } from '../../api/step3'
import { ACTIVE_FUNCTION, removeXRow } from './rldToStep3'
import { BALANCE_MIN_PCT } from '../../lib/composition'

interface Props {
  variables: XVariable[]
  totalWeight: number | null
  onChange: (vars: XVariable[]) => void
}

// 제형 기능 카탈로그(이슈 #27). 목록 외 값은 '사용자정의'로 자유 입력한다.
// `Active (API)` 는 #75(`M901-15`)에서 추가 — 대조약 전파 행과 같은 표기다. 수기로 골라도
// `is_active` 는 세우지 않는다(원래의 주성분이 아니므로, `Q-260916-06` ②).
const FUNCTIONS = [
  ACTIVE_FUNCTION,
  'Filler/Diluent',
  'Disintegrant',
  'Binder',
  'Surfactant',
  'Lubricant',
  'Glidant',
  'Coating agent',
  'Solution',
]
const CUSTOM = '__custom__'

const MODES: { value: XMode; label: string }[] = [
  { value: 'selected', label: 'Selected' },
  { value: 'selected(non-weight)', label: 'Selected (non-weight)' },
  { value: 'fixed', label: 'Fixed' },
  { value: 'balance', label: 'Balance' },
]

const emptyRow: XVariable = { excipient: '', function: '', mode: 'selected', min_pct: 0, max_pct: 0, sort_order: 0 }

// % 단위는 0~100, 소수점 2자리까지(회의 260521 결정 — 단위는 %로만, 그 이하는 저울로 계량 불가).
const clampPct = (n: number) => (Number.isFinite(n) ? Math.min(100, Math.max(0, n)) : 0)
const round2 = (n: number) => Math.round(n * 100) / 100
// mg→% 환산분은 셋째 자리에서 반올림한다(`M84-09`, 회의록 260804 L39). 직접 친 %는
// 종전대로 2자리다(REQ-FN-3-02 260629 — 0.01% 미만은 저울로 계량할 수 없다).
const round3 = (n: number) => Math.round(n * 1000) / 1000
// 소수 2자리 표시(불필요한 0 정리). 합계 안내·mg 환산 표시에 공용.
const fmt = (n: number) => round2(n).toString()

export default function XVariableSection({ variables, totalWeight, onChange }: Props) {
  const [rows, setRows] = useState<XVariable[]>(variables.length ? variables : [])
  // "사용자정의" 기능 입력을 펼친 행 인덱스. 비어 있어 카탈로그 외 값으로 판별되지 않는
  // 전환 직후 상태에서도 텍스트 입력을 노출하기 위한 보조 플래그.
  const [customRows, setCustomRows] = useState<Set<number>>(new Set())
  // 편집 중인 mg 칸 **한 개**의 원문. %를 정본으로 삼는 이상 mg 표시는 환산 왕복을 거치는데,
  // 그대로 두면 `1` 을 치는 순간 `1.001` 로 되튄다(1mg → 0.667% → 1.0005mg).
  const [mgDraft, setMgDraft] = useState<{ idx: number; field: 'min_pct' | 'max_pct'; raw: string } | null>(null)

  // variables prop이 외부에서 바뀌면(예: 대조약 선택으로 x_variables 교체) 표에 반영한다.
  // 내부 편집으로 인한 동일 내용 echo에는 반응하지 않아 입력 중 커서 튐을 막는다.
  useEffect(() => {
    setRows((prev) => (JSON.stringify(prev) === JSON.stringify(variables) ? prev : variables))
  }, [variables])

  const commit = (next: XVariable[]) => {
    setRows(next)
    onChange(next)
  }

  const update = (idx: number, patch: Partial<XVariable>) => {
    const next = [...rows]
    next[idx] = { ...next[idx], ...patch, sort_order: idx }
    commit(next)
  }

  // 모드별 min/max 입력 처리. 입력값은 0~100으로 클램프한다.
  // - fixed: min·max 중 하나를 바꾸면 다른 쪽도 동시에 같은 값으로 맞춘다.
  // - 그 외(balance 제외): 일단 해당 값만 반영하고, 정합(min ≤ max)·소수 2자리 보정은 blur/Enter 시.
  const changeBound = (idx: number, field: 'min_pct' | 'max_pct', raw: string) => {
    const val = clampPct(Number(raw))
    const mode = rows[idx].mode
    if (mode === 'fixed') {
      update(idx, { min_pct: val, max_pct: val })
    } else {
      update(idx, { [field]: val } as Partial<XVariable>)
    }
  }

  // mg 입력 → % 환산(`M84-09`). 저장 정본은 %이므로 여기서 바로 %로 바꿔 담는다.
  const changeBoundMg = (idx: number, field: 'min_pct' | 'max_pct', raw: string) => {
    setMgDraft({ idx, field, raw })
    if (!totalWeight || totalWeight <= 0) return
    const mg = Math.max(0, Number(raw) || 0)
    const val = clampPct(round3((mg * 100) / totalWeight))
    if (rows[idx].mode === 'fixed') update(idx, { min_pct: val, max_pct: val })
    else update(idx, { [field]: val } as Partial<XVariable>)
  }

  const normalizeBoundMg = (idx: number, field: 'min_pct' | 'max_pct') => {
    setMgDraft(null)
    normalizeBound(idx, field, round3)
  }

  // blur/Enter 시 소수 2자리로 정리하고, 방금 바꾼 값(field) 기준으로 min ≤ max를 보정한다.
  const normalizeBound = (idx: number, field: 'min_pct' | 'max_pct', rnd: (n: number) => number = round2) => {
    const r = rows[idx]
    if (r.mode === 'balance') return
    const min = rnd(r.min_pct)
    const max = rnd(r.max_pct)
    if (r.mode === 'fixed') {
      update(idx, { min_pct: min, max_pct: min })
      return
    }
    if (min <= max) {
      update(idx, { min_pct: min, max_pct: max })
    } else if (field === 'min_pct') {
      update(idx, { min_pct: min, max_pct: min })
    } else {
      update(idx, { min_pct: max, max_pct: max })
    }
  }

  // 숫자 입력에서 음수·지수 표기를 키 단계에서 차단한다('-', '+', 'e/E'). Enter는 blur로 정합 보정 트리거.
  // (clampPct가 0 미만을 0으로 보정하지만, 화면에 '-'가 잠깐 보이지 않도록 입력 자체를 막는다.)
  const onBoundKeyDown = (e: KeyboardEvent<HTMLInputElement>) => {
    if (e.key === '-' || e.key === '+' || e.key === 'e' || e.key === 'E') {
      e.preventDefault()
      return
    }
    if (e.key === 'Enter') e.currentTarget.blur()
  }

  const changeMode = (idx: number, mode: XMode) => {
    // fixed 전환 시 단일 값이 되도록 max를 min에 맞춘다.
    if (mode === 'fixed') update(idx, { mode, max_pct: rows[idx].min_pct })
    else update(idx, { mode })
  }

  const changeFunction = (idx: number, val: string) => {
    if (val === CUSTOM) {
      setCustomRows((s) => new Set(s).add(idx))
      update(idx, { function: '' })
    } else {
      setCustomRows((s) => {
        const n = new Set(s)
        n.delete(idx)
        return n
      })
      update(idx, { function: val })
    }
  }

  const addRow = () => commit([...rows, { ...emptyRow, sort_order: rows.length }])

  // `is_active` 행을 지우면 다음 `Active (API)` 행이 승계한다(`removeXRow`, Q-260916-06 ③).
  const removeRow = (idx: number) => {
    setCustomRows(new Set())
    commit(removeXRow(rows, idx))
  }

  const moveRow = (idx: number, dir: -1 | 1) => {
    const target = idx + dir
    if (target < 0 || target >= rows.length) return
    setCustomRows(new Set())
    const next = [...rows]
    ;[next[idx], next[target]] = [next[target], next[idx]]
    commit(next.map((r, i) => ({ ...r, sort_order: i })))
  }

  // total weight(mg) 기준 % → mg 환산. 총중량 미입력이면 mg 칸을 열지 않는다.
  const hasWeight = !!totalWeight && totalWeight > 0
  const mgValue = (idx: number, field: 'min_pct' | 'max_pct', pct: number): string => {
    if (mgDraft && mgDraft.idx === idx && mgDraft.field === field) return mgDraft.raw
    if (!hasWeight) return ''
    return String(round3((pct / 100) * (totalWeight as number)))
  }

  // 합계 검증(회의 260521·260629 결정).
  // - non-weight는 총량에 포함되지 않으므로 합계 계산에서 제외(0보다만 크면 100% 초과도 허용).
  // - balance는 fixed+selected 나머지를 채우므로, 있으면 max 합계가 100 미만이어도 무방.
  const weightRows = rows.filter((r) => r.mode === 'fixed' || r.mode === 'selected')
  const balanceRows = rows.filter((r) => r.mode === 'balance')
  const hasBalance = balanceRows.length >= 1
  const minSum = round2(weightRows.reduce((s, r) => s + (Number(r.min_pct) || 0), 0))
  const maxSum = round2(weightRows.reduce((s, r) => s + (Number(r.max_pct) || 0), 0))

  const errors: string[] = []
  // 2026-11-02(#101, BR-S3-03): balance 가 있으면 balance 하한 1% 자리를 남겨야 한다 — 하한 합 ≤ 99%.
  //   상한 합 > 100 은 막지 않는다 — 생성기가 합 제약을 지키고, MOBO 도 같은 공간을 쓴다.
  const minCap = hasBalance ? 100 - BALANCE_MIN_PCT : 100
  if (minSum > minCap) {
    errors.push(hasBalance
      ? `Sum of minimum % is ${fmt(minSum)}% — must not exceed ${fmt(minCap)}% (Fixed + Selected), leaving at least ${BALANCE_MIN_PCT}% for the Balance row.`
      : `Sum of minimum % is ${fmt(minSum)}% — must not exceed 100% (Fixed + Selected).`)
  }
  if (!hasBalance && maxSum < 100) {
    errors.push(`Sum of maximum % is ${fmt(maxSum)}% — must reach at least 100% when no Balance row is defined.`)
  }
  if (balanceRows.length >= 2) {
    errors.push(`${balanceRows.length} Balance rows defined — only one Balance row is allowed.`)
  }

  return (
    <div className="mb-4">
      <div className="mb-2 flex items-center justify-between">
        <h4 className="text-xs font-semibold uppercase tracking-wider text-text-secondary">
          X Variables (Excipient &amp; Function)
        </h4>
        <div className="flex gap-2">
          <button
            onClick={addRow}
            className="rounded px-2 py-1 text-[10px] text-text-secondary transition-colors hover:bg-bg-input"
            style={{ border: '1px solid var(--border-soft)' }}
          >
            + Excipient
          </button>
        </div>
      </div>
      <div className="overflow-x-auto rounded border" style={{ borderColor: 'var(--border-subtle)' }}>
        <table className="w-full text-[11px]">
          <thead>
            <tr style={{ backgroundColor: 'var(--color-bg-panel)' }}>
              <th className="px-2 py-1.5 text-left font-medium text-text-secondary">Function</th>
              <th className="px-2 py-1.5 text-left font-medium text-text-secondary">Excipient</th>
              <th className="px-2 py-1.5 text-center font-medium text-text-secondary">Mode</th>
              {/* 열 순서 정본 = `min mg, max mg, min %, max %`(`M84-09`, 회의록 260804 L39). */}
              <th className="w-20 px-2 py-1.5 text-center font-medium text-text-secondary">Min (mg)</th>
              <th className="w-20 px-2 py-1.5 text-center font-medium text-text-secondary">Max (mg)</th>
              <th className="w-16 px-2 py-1.5 text-center font-medium text-text-secondary">Min %</th>
              <th className="w-16 px-2 py-1.5 text-center font-medium text-text-secondary">Max %</th>
              <th className="w-20 px-2 py-1.5 text-center font-medium text-text-secondary" />
            </tr>
          </thead>
          <tbody>
            {rows.map((row, i) => {
              const isCustom = customRows.has(i) || (!!row.function && !FUNCTIONS.includes(row.function))
              const disabledBounds = row.mode === 'balance'
              // mg 는 총중량이 있어야 %로 되돌릴 수 있다. 없으면 칸을 잠그고 사유를 남긴다.
              const mgDisabled = disabledBounds || !hasWeight
              const mgTitle = disabledBounds
                ? 'Balance — 입력 불가(잔량 자동 충전)'
                : !hasWeight ? '총중량(§3.1)을 먼저 입력하십시오' : undefined
              return (
                <tr key={i} className="border-t" style={{ borderColor: 'var(--border-subtle)' }}>
                  <td className="px-1 py-1">
                    <div className="flex flex-col gap-1">
                      <select
                        value={isCustom ? CUSTOM : row.function}
                        onChange={(e) => changeFunction(i, e.target.value)}
                        className="w-full rounded bg-bg-input px-1 py-1 text-[11px] text-text-body outline-none"
                      >
                        <option value="">Select…</option>
                        {FUNCTIONS.map((f) => (
                          <option key={f} value={f}>{f}</option>
                        ))}
                        <option value={CUSTOM}>Custom…</option>
                      </select>
                      {isCustom && (
                        <input
                          type="text"
                          value={row.function}
                          onChange={(e) => update(i, { function: e.target.value })}
                          className="w-full rounded bg-bg-input px-2 py-1 text-[11px] text-text-body outline-none"
                          placeholder="사용자정의 기능"
                        />
                      )}
                    </div>
                  </td>
                  <td className="px-1 py-1">
                    <input
                      type="text"
                      value={row.excipient}
                      onChange={(e) => update(i, { excipient: e.target.value })}
                      className="w-full rounded bg-bg-input px-2 py-1 text-[11px] text-text-body outline-none"
                      placeholder="e.g. HPMC"
                    />
                  </td>
                  <td className="px-1 py-1 text-center">
                    <select
                      value={row.mode}
                      onChange={(e) => changeMode(i, e.target.value as XMode)}
                      className="rounded bg-bg-input px-1 py-1 text-[11px] text-text-body outline-none"
                    >
                      {MODES.map((m) => (
                        <option key={m.value} value={m.value}>{m.label}</option>
                      ))}
                    </select>
                  </td>
                  <td className="px-1 py-1">
                    <input
                      type="number"
                      min={0}
                      step={0.001}
                      value={mgDisabled ? '' : mgValue(i, 'min_pct', row.min_pct)}
                      onChange={(e) => changeBoundMg(i, 'min_pct', e.target.value)}
                      onBlur={() => normalizeBoundMg(i, 'min_pct')}
                      onKeyDown={onBoundKeyDown}
                      disabled={mgDisabled}
                      title={mgTitle}
                      className="w-full rounded bg-bg-input px-2 py-1 text-center text-[11px] text-text-body outline-none disabled:opacity-40"
                      placeholder={mgDisabled ? '—' : '0'}
                    />
                  </td>
                  <td className="px-1 py-1">
                    <input
                      type="number"
                      min={0}
                      step={0.001}
                      value={mgDisabled ? '' : mgValue(i, 'max_pct', row.max_pct)}
                      onChange={(e) => changeBoundMg(i, 'max_pct', e.target.value)}
                      onBlur={() => normalizeBoundMg(i, 'max_pct')}
                      onKeyDown={onBoundKeyDown}
                      disabled={mgDisabled}
                      title={mgTitle}
                      className="w-full rounded bg-bg-input px-2 py-1 text-center text-[11px] text-text-body outline-none disabled:opacity-40"
                      placeholder={mgDisabled ? '—' : '0'}
                    />
                  </td>
                  <td className="px-1 py-1">
                    <input
                      type="number"
                      min={0}
                      max={100}
                      step={0.01}
                      value={disabledBounds ? '' : row.min_pct}
                      onChange={(e) => changeBound(i, 'min_pct', e.target.value)}
                      onBlur={() => normalizeBound(i, 'min_pct')}
                      onKeyDown={onBoundKeyDown}
                      disabled={disabledBounds}
                      title={disabledBounds ? 'Balance — 입력 불가(잔량 자동 충전)' : undefined}
                      className="w-full rounded bg-bg-input px-2 py-1 text-center text-[11px] text-text-body outline-none disabled:opacity-40"
                      placeholder={disabledBounds ? '—' : '0'}
                    />
                  </td>
                  <td className="px-1 py-1">
                    <input
                      type="number"
                      min={0}
                      max={100}
                      step={0.01}
                      value={disabledBounds ? '' : row.max_pct}
                      onChange={(e) => changeBound(i, 'max_pct', e.target.value)}
                      onBlur={() => normalizeBound(i, 'max_pct')}
                      onKeyDown={onBoundKeyDown}
                      disabled={disabledBounds}
                      title={disabledBounds ? 'Balance — 입력 불가(잔량 자동 충전)' : undefined}
                      className="w-full rounded bg-bg-input px-2 py-1 text-center text-[11px] text-text-body outline-none disabled:opacity-40"
                      placeholder={disabledBounds ? '—' : '0'}
                    />
                  </td>
                  <td className="px-1 py-1 text-center">
                    <div className="flex items-center justify-center gap-1">
                      <button onClick={() => moveRow(i, -1)} className="text-text-secondary hover:text-text-body text-[10px] disabled:opacity-30" disabled={i === 0}>
                        ↑
                      </button>
                      <button onClick={() => moveRow(i, 1)} className="text-text-secondary hover:text-text-body text-[10px] disabled:opacity-30" disabled={i === rows.length - 1}>
                        ↓
                      </button>
                      <button onClick={() => removeRow(i)} className="text-text-secondary hover:text-danger text-[10px] ml-1">
                        ✕
                      </button>
                    </div>
                  </td>
                </tr>
              )
            })}
            {rows.length === 0 && (
              <tr>
                <td colSpan={8} className="px-3 py-3 text-center text-text-secondary text-[11px]">
                  No excipients added. Click "+ Excipient" to start.
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
      {/* 합계 안내(영문). Fixed+Selected 기준 합계와 위반 항목을 표시한다. non-weight는 제외. */}
      <div className="mt-2 space-y-0.5 text-[10px]">
        <p className="text-text-muted">
          Sum (Fixed + Selected) — Min: {fmt(minSum)}% · Max: {fmt(maxSum)}%
          {hasBalance ? ` · Balance × ${balanceRows.length}` : ' · no Balance'}
        </p>
        {errors.map((msg, k) => (
          <p key={k} className="text-danger">⚠ {msg}</p>
        ))}
        {errors.length === 0 && rows.length > 0 && (
          <p style={{ color: 'var(--color-accent-teal)' }}>✓ Composition bounds are consistent.</p>
        )}
      </div>
    </div>
  )
}
